import classnames from 'classnames';
import type { UIPartyCharacter } from '@sorrem-client/types';
import type { BodyPart, EquipmentPiece } from '@sorrem/shared';
import type { ReactNode } from 'react';
import type { SkillTreeViewStore } from '@sorrem-client/ui/features/SkillTree';
import {
    useCarriedSlotHotkeyAssignment,
    useCraftingWorkbench,
    useCrucibleSelection,
    useJourneyState,
    useSkillHotkeyAssignment,
    type CharacterCreationDraft,
} from '@sorrem-client/ui/features/CharacterHome/hooks';
import {
    CarriedSlotHotkeyHint,
    CraftingMainPanel,
    CraftingVesselPanel,
    CrucibleInfoPanel,
    CruciblesLockedNotice,
    CruciblesMainPanel,
    FeaturedCruciblesPanel,
    InventoryItemPanel,
    InventoryMainPanel,
    InventoryStoragePanel,
    JourneyMainPanel,
    SkillHotkeyHint,
    SkillsDetailPanel,
    SkillsMainPanel,
    type CharacterHomeTab,
} from '@sorrem-client/ui/features/CharacterHome/tabs';
import { CharacterHomeItemTransferProvider } from '../CharacterHomeItemTransfer';
import { CharacterHomeLoadout } from '../CharacterHomeLoadout';
import { CharacterSummaryPanel } from '../CharacterSummaryPanel';
import { CreationActionsPanel } from '../CreationActionsPanel';
import { CreationAppearancePanel } from '../CreationAppearancePanel';
import { CreationIdentityPanel } from '../CreationIdentityPanel';
import { SlidePanel, type SlideDirection } from '../SlidePanel';
import styles from './CharacterHomePanels.module.scss';

interface SlidePanelDef {
    readonly key: string;
    readonly className: string;
    readonly direction: SlideDirection;
    readonly visible: boolean;
    readonly content: ReactNode;
}

interface CharacterHomePanelsProps {
    readonly player: UIPartyCharacter | null;
    readonly isPlayerLoading: boolean;
    readonly playerError: string | null;
    readonly isCreationViewShown: boolean;
    readonly activeTab: CharacterHomeTab;
    /** The crucible the route links to, if any. */
    readonly selectedCrucibleId: string | undefined;
    /** The invite code the route carries, if any; it links to the crucible the code names. */
    readonly inviteCode: string | undefined;
    readonly draft: CharacterCreationDraft;
    readonly bodyParts: BodyPart[];
    readonly equipmentPieces: EquipmentPiece[];
    /** The skill tree's pan/zoom, for the Skills tab's zoom buttons. */
    readonly skillTreeViewStore: SkillTreeViewStore;
    /** The selected skill or dial (`dial:<branch>`); its details show in the right panel. */
    readonly selectedSkillTreeId: string | null;
    /** Whether the home is drawn, no longer behind its loading message. Until it is, every panel stays hidden. */
    readonly isHomeShown: boolean;
}

/**
 * Character Home's slide panels, each sliding in or out by the tab and
 * whether a character is being created: the side panels, the tabs' main
 * panels, the loadout along the bottom, and the hotkey hints over it. They
 * share one item transfer selection.
 */
export function CharacterHomePanels({
    player,
    isPlayerLoading,
    playerError,
    isCreationViewShown,
    activeTab,
    selectedCrucibleId,
    inviteCode,
    draft,
    bodyParts,
    equipmentPieces,
    skillTreeViewStore,
    selectedSkillTreeId,
    isHomeShown,
}: CharacterHomePanelsProps): JSX.Element {
    const isTab = (tab: CharacterHomeTab): boolean => !isCreationViewShown && activeTab === tab;
    const isSkillsTab = isTab('skills');
    const isJourneyTab = isTab('journey');
    // Crafting gives its panels the full height: the loadout slides away, and equipped items are picked in its strip.
    const isCraftingTab = isTab('crafting');
    const crucibleSelection = useCrucibleSelection(selectedCrucibleId, inviteCode);
    const journey = useJourneyState();
    // The workbench picks in the middle and the vessel panel acts on the right.
    const craftingWorkbench = useCraftingWorkbench(player?.id ?? null);
    // "Assign to a hotkey" brings the loadout up over the tree to pick an action slot.
    const skillHotkeyAssignment = useSkillHotkeyAssignment(isSkillsTab, selectedSkillTreeId);
    // A carried slot's key label on the Inventory tab waits for an action slot to link it to.
    const carriedSlotHotkeyAssignment = useCarriedSlotHotkeyAssignment(isTab('inventory'));

    const panels: SlidePanelDef[] = [
        {
            key: 'summary',
            className: classnames(styles.region, styles.left, styles.fullHeightable, isCraftingTab && styles.fullHeight),
            direction: 'left',
            visible: !isCreationViewShown && !isSkillsTab && !isJourneyTab,
            content: <CharacterSummaryPanel player={player} isLoading={isPlayerLoading} error={playerError} />,
        },
        {
            key: 'creation-identity',
            className: classnames(styles.region, styles.left, styles.creationIdentity),
            direction: 'left',
            visible: isCreationViewShown,
            content: <CreationIdentityPanel draft={draft} />,
        },
        {
            key: 'inventory',
            className: classnames(styles.region, styles.main),
            direction: 'down',
            visible: isTab('inventory'),
            content: (
                <>
                    <InventoryMainPanel
                        linkingSlotIndex={carriedSlotHotkeyAssignment.linkingSlotIndex}
                        onLinkingSlotChange={carriedSlotHotkeyAssignment.setLinkingSlotIndex}
                    />
                    <InventoryStoragePanel />
                </>
            ),
        },
        {
            key: 'skills',
            className: classnames(styles.region, styles.skillsPanel, styles.passThrough),
            direction: 'down',
            visible: isSkillsTab,
            content: <SkillsMainPanel viewStore={skillTreeViewStore} />,
        },
        {
            key: 'crucibles',
            className: classnames(styles.region, styles.main),
            direction: 'down',
            visible: isTab('crucibles'),
            content: crucibleSelection.isCruciblesLocked ? (
                <CruciblesLockedNotice />
            ) : (
                <CruciblesMainPanel
                    crucibles={crucibleSelection.crucibles}
                    hasMoreCrucibles={crucibleSelection.hasMoreCrucibles}
                    isLoadingMoreCrucibles={crucibleSelection.isLoadingMoreCrucibles}
                    onShowMoreCrucibles={crucibleSelection.showMoreCrucibles}
                    selectedCrucibleId={crucibleSelection.selectedCrucible?.id ?? null}
                    onSelectCrucible={crucibleSelection.selectCrucible}
                    onCreateDraftChange={crucibleSelection.setCreateDraft}
                />
            ),
        },
        {
            key: 'crafting',
            className: classnames(styles.region, styles.main, styles.fullHeight),
            direction: 'down',
            visible: isTab('crafting'),
            content: <CraftingMainPanel workbench={craftingWorkbench} />,
        },
        {
            key: 'journey',
            className: classnames(styles.region, styles.journeyPanel),
            direction: 'down',
            visible: isJourneyTab,
            content: (
                <JourneyMainPanel
                    section={journey.section}
                    onSectionChange={journey.setSection}
                    selectedEntryId={journey.selectedEntryId}
                    onSelectEntry={journey.setSelectedEntryId}
                    compendiumSelection={journey.compendiumSelection}
                    onSelectCompendium={journey.setCompendiumSelection}
                />
            ),
        },
        {
            key: 'featured-crucibles',
            className: classnames(styles.region, styles.right),
            direction: 'right',
            visible: isTab('overview'),
            content: <FeaturedCruciblesPanel />,
        },
        {
            key: 'inventory-item',
            className: classnames(styles.region, styles.right),
            direction: 'right',
            visible: isTab('inventory'),
            content: <InventoryItemPanel />,
        },
        {
            key: 'crafting-vessel',
            className: classnames(styles.region, styles.right, styles.fullHeight),
            direction: 'right',
            visible: isTab('crafting'),
            content: <CraftingVesselPanel workbench={craftingWorkbench} />,
        },
        {
            key: 'crucible-info',
            className: classnames(styles.region, styles.right),
            direction: 'right',
            visible: isTab('crucibles') && !crucibleSelection.isCruciblesLocked,
            content: (
                <CrucibleInfoPanel
                    crucible={crucibleSelection.selectedCrucible}
                    unavailableNotice={crucibleSelection.selectedCrucible === null ? crucibleSelection.unavailableNotice : null}
                    createDraft={crucibleSelection.createDraft}
                />
            ),
        },
        {
            key: 'skills-detail',
            className: classnames(styles.region, styles.right, styles.skillsDetail, skillHotkeyAssignment.isLoadoutShown && styles.withLoadout),
            direction: 'right',
            visible: isSkillsTab && selectedSkillTreeId !== null,
            content: (
                <SkillsDetailPanel
                    selectedId={selectedSkillTreeId}
                    assigningSkillId={skillHotkeyAssignment.assigningSkillId}
                    onAssigningSkillChange={skillHotkeyAssignment.setAssigningSkillId}
                />
            ),
        },
        {
            key: 'creation-appearance',
            className: classnames(styles.region, styles.right, styles.creationAppearance),
            direction: 'right',
            visible: isCreationViewShown,
            content: <CreationAppearancePanel draft={draft} bodyParts={bodyParts} equipmentPieces={equipmentPieces} />,
        },
        {
            // The Skills tab gives the whole screen to the tree (except while a skill picks its action slot), and the Journey to its books.
            key: 'loadout',
            className: classnames(styles.region, styles.loadoutPanel),
            direction: 'down',
            visible: !isCreationViewShown && ((!isSkillsTab && !isJourneyTab && !isCraftingTab) || skillHotkeyAssignment.isLoadoutShown),
            content: (
                <CharacterHomeLoadout
                    className={styles.loadout}
                    assigningSkillId={skillHotkeyAssignment.assigningSkillId}
                    onAssignSkill={skillHotkeyAssignment.assign}
                    confirmedActionIndex={skillHotkeyAssignment.confirmedHotkeyIndex}
                    linkingSlotIndex={carriedSlotHotkeyAssignment.linkingSlotIndex}
                    onLinkCarriedSlot={carriedSlotHotkeyAssignment.link}
                />
            ),
        },
    ];

    return (
        <CharacterHomeItemTransferProvider character={player}>
            {panels.map((panel) => (
                // Every panel starts hidden and slides in once the home is drawn.
                <SlidePanel key={panel.key} className={panel.className} direction={panel.direction} isVisible={isHomeShown && panel.visible}>
                    {panel.content}
                </SlidePanel>
            ))}
            <SkillHotkeyHint className={classnames(styles.region, styles.hotkeyHint)} skillId={skillHotkeyAssignment.assigningSkillId} />
            <CarriedSlotHotkeyHint className={classnames(styles.region, styles.hotkeyHint)} slotIndex={carriedSlotHotkeyAssignment.linkingSlotIndex} />
            <SlidePanel className={classnames(styles.region, styles.creationActions)} direction="down" isVisible={isHomeShown && isCreationViewShown}>
                <CreationActionsPanel draft={draft} />
            </SlidePanel>
        </CharacterHomeItemTransferProvider>
    );
}
